import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

function VerificationComplete() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const pollCount = useRef(0);
  const maxPolls = 10; // Max 10 attempts (20 seconds)

  useEffect(() => {
    checkVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // First: Get the current KYC status and session ID
      const statusResponse = await fetch('/api/kyc/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to check status');
      }

      const statusData = await statusResponse.json();
      
      // If already verified in DB, we're done
      if (statusData.kycStatus === 'verified') {
        user.accountTier = 'verified';
        user.kycStatus = 'verified';
        localStorage.setItem('user', JSON.stringify(user));
        navigate('/seller-portal');
        return;
      }
      
      // If pending and we have a session ID, check with Stripe to get real status
      if (statusData.kycStatus === 'pending' && statusData.sessionId) {
        const verifyResponse = await fetch(`/api/kyc/verification-status/${statusData.sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (verifyResponse.ok) {
          const verifyData = await verifyResponse.json();
          
          // Check the Stripe session status
          if (verifyData.status === 'verified') {
            user.accountTier = 'verified';
            user.kycStatus = 'verified';
            localStorage.setItem('user', JSON.stringify(user));
            navigate('/seller-portal');
            return;
          }
          
          if (verifyData.status === 'requires_input') {
            setStatus('failed');
            return;
          }
          
          // Still processing on Stripe's end
          if (verifyData.status === 'processing') {
            pollCount.current += 1;
            
            if (pollCount.current < maxPolls) {
              setTimeout(checkVerificationStatus, 2000);
            } else {
              setStatus('pending');
            }
            return;
          }
        }
        
        // If we couldn't get Stripe status, poll again
        pollCount.current += 1;
        if (pollCount.current < maxPolls) {
          setTimeout(checkVerificationStatus, 2000);
        } else {
          setStatus('pending');
        }
        return;
      }
      
      if (statusData.kycStatus === 'failed' || statusData.kycStatus === 'requires_input') {
        setStatus('failed');
        return;
      }
      
      // Any other status (not_started, etc.)
      setStatus('not_verified');
      
    } catch (err) {
      console.error('Error checking status:', err);
      setStatus('error');
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: '#f9fafb'
    }}>
      <div style={{ 
        maxWidth: '500px', 
        width: '100%',
        padding: '48px 40px',
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        {/* Checking status */}
        {status === 'checking' && (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #111827',
              borderRadius: '50%',
              margin: '0 auto 24px',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{ color: '#6b7280' }}>Checking verification status...</p>
          </>
        )}

        {/* Verified */}
        {status === 'verified' && (
          <>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              marginBottom: '16px',
              color: '#111827'
            }}>
              Identity Verified!
            </h1>
            
            <p style={{ 
              fontSize: '16px', 
              color: '#6b7280',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              Your account has been successfully verified. You can now list your property and access all features.
            </p>

            <button
              onClick={() => navigate('/seller-portal')}
              style={{
                padding: '14px 32px',
                background: '#111827',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </>
        )}

        {/* Pending - still processing */}
        {status === 'pending' && (
          <>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              marginBottom: '16px',
              color: '#111827'
            }}>
              Verification Processing
            </h1>
            
            <p style={{ 
              fontSize: '16px', 
              color: '#6b7280',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              Your verification is still being processed. This usually takes a few minutes. 
              You'll receive an email once it's complete.
            </p>

            <button
              onClick={() => navigate('/seller-portal')}
              style={{
                padding: '14px 32px',
                background: '#111827',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </>
        )}

        {/* Failed */}
        {status === 'failed' && (
          <>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              marginBottom: '16px',
              color: '#111827'
            }}>
              Verification Failed
            </h1>
            
            <p style={{ 
              fontSize: '16px', 
              color: '#6b7280',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              We couldn't verify your identity. This might happen if the photos were unclear or the ID was unreadable. Please try again.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/verify-account')}
                style={{
                  padding: '14px 32px',
                  background: '#111827',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>

              <button
                onClick={() => navigate('/seller-portal')}
                style={{
                  padding: '14px 32px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </>
        )}

        {/* Not Verified / Incomplete */}
        {status === 'not_verified' && (
          <>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              marginBottom: '16px',
              color: '#111827'
            }}>
              Verification Incomplete
            </h1>
            
            <p style={{ 
              fontSize: '16px', 
              color: '#6b7280',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              Your identity verification was not completed. You can still browse listings, but you'll need to verify your identity before listing a property.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/verify-account')}
                style={{
                  padding: '14px 32px',
                  background: '#111827',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Start Verification
              </button>

              <button
                onClick={() => navigate('/seller-portal')}
                style={{
                  padding: '14px 32px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              marginBottom: '16px',
              color: '#111827'
            }}>
              Something Went Wrong
            </h1>
            
            <p style={{ 
              fontSize: '16px', 
              color: '#6b7280',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              We couldn't check your verification status. Please try again.
            </p>

            <button
              onClick={() => navigate('/seller-portal')}
              style={{
                padding: '14px 32px',
                background: '#111827',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </>
        )}

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
}

export default VerificationComplete;
